import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  Avatar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Edit, Delete, CloudUpload, Close } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import httpClient from "../Api/axios";

function GalleryMaster() {
  const [images, setImages] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [formData, setFormData] = useState({ text: "", img: "" });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const fileInputRefCover = useRef(null);

  const fetchImages = async () => {
    try {
      const res = await httpClient.get("/gallery");
      // Reverse the array to show newest items first
      setImages([...res.data].reverse());
      console.log("Fetched images:", res.data);
    } catch (err) {
      toast.error("Failed to fetch images");
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleOpenDialog = (image = null) => {
    setCurrentImage(image);
    if (image) {
      setFormData({ text: image.text, img: image.img });
      setPreviewUrl(image.img);
    } else {
      setFormData({ text: "", img: "" });
      setPreviewUrl(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setFormData({ text: "", img: "" });
    setPreviewUrl(null);
    setCurrentImage(null);
    setOpenDialog(false);
    if (fileInputRefCover.current) fileInputRefCover.current.value = "";
  };

  const handleFileChange = async (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2MB");
      return;
    }

    const form = new FormData();
    form.append("image", file);

    try {
      const res = await httpClient.post("/file_upload", form, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data && res.data.path) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          img: res.data.path,
        }));
        setPreviewUrl(res.data.path);
      } else {
        toast.error("Invalid response from server");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading image: " + (error.response?.data?.message || error.message));
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.img) {
        toast.error("Please upload an image.");
        return;
      }
  
      if (!formData.text.trim()) {
        toast.error("Please enter a text.");
        return;
      }
  
      console.log("Saving Data:", formData);
  
      if (currentImage) {
        // PUT
        const res = await httpClient.put(`/gallery/${currentImage._id}`);
        console.log("PUT Response:", res.data);
        
        toast.success("Image updated successfully");
      } else {
        // POST
        const res = await httpClient.post("/gallery", formData);
        console.log("POST Response:", res.data);
        toast.success("Image added successfully");
      }
  
      fetchImages();
      handleCloseDialog();
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      toast.error(err.response?.data?.message || "Something went wrong!");
    }
  };
  
  
  const handleDelete = (image) => {
    setCurrentImage(image);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await httpClient.delete(`/gallery/${currentImage._id}`);
      toast.success("Image deleted");
      fetchImages();
    } catch (err) {
      toast.error("Failed to delete image");
    }
    setDeleteDialog(false);
  };

  const handleButtonClickCover = () => {
    fileInputRefCover.current.click();
  };

  const columns = [
    {
      field: "img",
      headerName: "Image",
      flex: 1,
      renderCell: (params) => (
        <Avatar
          variant="rounded"
          src={params.value}
          alt="Preview"
          sx={{ width: 60, height: 60 }}
        />
      ),
    },
    { field: "text", headerName: "Text", flex: 2 },
    {
      field: "actions",
      headerName: "Action",
      flex: 2,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Edit />}
            onClick={() => handleOpenDialog(params.row)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box p={4}>
      <ToastContainer position="top-right" />
      <Stack direction="row" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          Gallery Master
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => handleOpenDialog()}
        >
          Add New Image
        </Button>
      </Stack>

      <Box sx={{ height: 500 }}>
        <DataGrid
          rows={images}
          getRowId={(row) => row._id}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          disableSelectionOnClick
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {currentImage ? "Edit Image" : "Add New Image"}
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Text"
              fullWidth
              value={formData.text}
              onChange={(e) => setFormData((prev) => ({ ...prev, text: e.target.value }))}
            />
            <Box>
              {previewUrl ? (
                <Avatar
                  src={previewUrl}
                  variant="rounded"
                  sx={{ width: "100%", height: 200, mb: 1 }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: 200,
                    bgcolor: "#f0f0f0",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 1,
                    border: "1px dashed #ccc",
                    borderRadius: 1
                  }}
                >
                  <Typography color="textSecondary">No Image Selected</Typography>
                </Box>
              )}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={fileInputRefCover}
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleButtonClickCover}
                fullWidth
              >
                Browse Image
              </Button>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.text || !formData.img}
          >
            {currentImage ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this image?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GalleryMaster;